import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AdminLogin from "../components/admin/AdminLogin";
import LeadsTable from "../components/admin/LeadsTable";
import LeadNotesDialog from "../components/admin/LeadNotesDialog";
import DeleteLeadDialog from "../components/admin/DeleteLeadDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

export default function AdminLeads() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [leads, setLeads] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);

  useEffect(() => {
    document.title = "Leads admin · The Formulation House";
    const meta = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" });
    document.head.appendChild(meta);
    http.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!user) return;
    http.get("/leads").then(({ data }) => setLeads(data)).catch(() => setUser(false));
  }, [user]);

  const patch = async (id, body, successMsg) => {
    try {
      const { data } = await http.patch(`/leads/${id}`, body);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(successMsg);
      return true;
    } catch {
      toast.error("Could not save. Please try again.");
      return false;
    }
  };

  const updateStatus = async (id, status) => {
    setSavingId(id);
    await patch(id, { status }, `Marked as ${status}`);
    setSavingId(null);
  };

  const saveNotes = (id, body) => patch(id, body, "Follow-up saved");

  const [digesting, setDigesting] = useState(false);
  const sendDigest = async () => {
    setDigesting(true);
    try {
      const { data } = await http.post("/admin/digest/send");
      if (data.sent) toast.success(`Digest sent — ${data.count} lead${data.count > 1 ? "s" : ""} to ${data.to.length} recipient${data.to.length > 1 ? "s" : ""}`);
      else if (data.reason === "nothing due") toast.info("Nothing due today — no digest sent");
      else toast.error(`Digest not sent: ${data.reason}`);
    } catch {
      toast.error("Could not send digest. Please try again.");
    } finally {
      setDigesting(false);
    }
  };

  const confirmDelete = async (id) => {
    try {
      await http.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete. Please try again.");
    } finally {
      setDeleteLead(null);
    }
  };

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
    setLeads(null);
  };

  if (user === null || (user && leads === null)) {
    return (
      <div data-testid="admin-loading" className="min-h-screen grid place-items-center bg-ivory text-navy">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }
  if (!user) return <AdminLogin onSuccess={setUser} />;
  return (
    <>
      <LeadsTable leads={leads} user={user} onLogout={logout} onStatus={updateStatus} savingId={savingId} onNotes={setNotesLead} onDelete={setDeleteLead} onDigest={sendDigest} digesting={digesting} />
      <LeadNotesDialog lead={notesLead} onClose={() => setNotesLead(null)} onSave={saveNotes} />
      <DeleteLeadDialog lead={deleteLead} onClose={() => setDeleteLead(null)} onConfirm={confirmDelete} />
    </>
  );
}
